/**
 * SQLite Storage Service
 * 
 * This service provides an interface to the SQLite database via the backend API.
 * It uses the same method signatures as the LocalStorage service, but connects to SQLite.
 */

import axios from 'axios';

const API_BASE_URL = (() => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
})();

/**
 * Service for handling SQLite database operations via the API
 */
class SQLiteStorageService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    // Store auth token getter function
    this.getAuthToken = null;
    
    // Cache configuration
    this.cache = new Map();
    this.cacheTimestamps = new Map();
    this.cacheTTL = 30000; // 30 seconds cache TTL
  }
  
  /**
   * Set the auth token getter function
   */
  setAuthTokenGetter(getAuthToken) {
    this.getAuthToken = getAuthToken;
  }
  
  /**
   * Check if cache entry is still valid
   */
  isCacheValid(key) {
    const timestamp = this.cacheTimestamps.get(key);
    if (!timestamp) return false;
    return Date.now() - timestamp < this.cacheTTL;
  }
  
  /**
   * Get from cache if valid
   */
  getFromCache(key) {
    if (this.isCacheValid(key)) {
      return this.cache.get(key);
    }
    this.cache.delete(key);
    this.cacheTimestamps.delete(key);
    return null;
  }
  
  /**
   * Set cache entry
   */
  setCache(key, value) {
    this.cache.set(key, value);
    this.cacheTimestamps.set(key, Date.now());
  }
  
  /**
   * Clear all cache
   */
  clearCache() {
    this.cache.clear();
    this.cacheTimestamps.clear();
  }
  
  /**
   * Clear cache for specific pattern
   */
  clearCachePattern(pattern) {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.cacheTimestamps.delete(key);
    });
  }
  
  /**
   * Get authorization headers - waits for auth token getter to be available
   */
  async getAuthHeaders(forceRefresh = false) {
    // Wait up to 4 seconds for auth token getter to be available
    const maxWaitTime = 4000; // 4 seconds
    const checkInterval = 100; // 100ms
    let waitTime = 0;
    
    while (!this.getAuthToken && waitTime < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      waitTime += checkInterval;
    }
    
    if (!this.getAuthToken) {
      console.error('SQLiteStorageService: Auth token getter not available after waiting - requests will fail with 401');
      throw new Error('Authentication not available - please wait for login to complete');
    }
    
    try {
      const token = await this.getAuthToken(forceRefresh);
      if (!token) {
        console.error('SQLiteStorageService: Auth token getter returned null/undefined - user not authenticated');
        throw new Error('User not authenticated - please login');
      }
      console.log('SQLiteStorageService: Successfully got auth token for API request');
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.error('SQLiteStorageService: Failed to get auth token:', error);
      throw error;
    }
  }

  /**
   * Execute an API request with automatic token refresh retry on auth failures.
   * @param {Function} requestFn - Function that receives auth headers and performs the request
   * @param {Object} options
   * @param {boolean} options.retryOnAuthFailure - Whether to retry once on 401/403 responses
   */
  async withAuthRequest(requestFn, { retryOnAuthFailure = true } = {}) {
    const execute = async (forceRefresh = false) => {
      const headers = await this.getAuthHeaders(forceRefresh);
      return requestFn(headers);
    };

    try {
      return await execute(false);
    } catch (error) {
      const status = error?.response?.status;
      const shouldRetry =
        retryOnAuthFailure && status && [401, 403].includes(status);

      if (!shouldRetry) {
        throw error;
      }

      console.warn(
        `SQLiteStorageService: Auth failure (${status}) detected, forcing token refresh and retrying request`
      );

      try {
        return await execute(true);
      } catch (retryError) {
        console.error(
          'SQLiteStorageService: Retry after token refresh failed',
          retryError
        );
        throw retryError;
      }
    }
  }

  // Check if the database is available
  async isDatabaseAvailable() {
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.get('/database/status', { headers })
      );
      return response.data.status === 'ok';
    } catch (error) {
      console.error('SQLiteStorageService: Database connectivity check failed', error);
      return false;
    }
  }

  // Project operations
  async getProjects() {
    const cacheKey = 'projects:all';
    
    // Check cache first
    const cached = this.getFromCache(cacheKey);
    if (cached !== null) {
      console.log('SQLiteStorageService: Returning cached projects');
      return cached;
    }
    
    console.log('SQLiteStorageService: Getting all projects from database');
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.get('/projects', { headers })
      );
      const projects = response.data || [];
      
      // Cache the result
      this.setCache(cacheKey, projects);
      
      return projects;
    } catch (error) {
      console.error('SQLiteStorageService: Error getting projects', error);
      // If we get a 403 or 401, clear cache as user permissions might have changed
      if (error.response && (error.response.status === 403 || error.response.status === 401)) {
        console.log('SQLiteStorageService: Got auth error on projects, clearing all cache');
        this.clearCache();
      }
      return [];
    }
  }

  async getProject(projectId) {
    const cacheKey = `project:${projectId}`;
    
    // Check cache first
    const cached = this.getFromCache(cacheKey);
    if (cached !== null) {
      console.log(`SQLiteStorageService: Returning cached project ${projectId}`);
      return cached;
    }
    
    console.log(`SQLiteStorageService: Getting project ${projectId} from database`);
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.get(`/projects/${projectId}`, { headers })
      );
      const project = response.data;
      
      // Cache the result
      if (project) {
        this.setCache(cacheKey, project);
      }
      
      return project;
    } catch (error) {
      console.error(`SQLiteStorageService: Error getting project ${projectId}`, error);
      // If we get a 403, clear cache as user permissions might have changed
      if (error.response && error.response.status === 403) {
        console.log('SQLiteStorageService: Got 403, clearing all cache');
        this.clearCache();
      }
      return null;
    }
  }

  async saveProject(project) {
    console.log(`SQLiteStorageService: Saving project with ID ${project.id}`);
    try {
      let response;
      const requestFn = (headers) => {
        if (project.id) {
          return this.api.put(`/projects/${project.id}`, project, { headers });
        }
        return this.api.post('/projects', project, { headers });
      };
      response = await this.withAuthRequest(requestFn);
      console.log('SQLiteStorageService: Project saved successfully');
      
      // Clear related cache
      this.clearCachePattern('projects:');
      this.clearCachePattern(`project:${project.id}`);
      
      return response.data;
    } catch (error) {
      console.error(`SQLiteStorageService: Error saving project ${project.id}`, error);
      throw error;
    }
  }

  async deleteProject(projectId) {
    console.log(`SQLiteStorageService: Deleting project ${projectId}`);
    try {
      await this.withAuthRequest((headers) =>
        this.api.delete(`/projects/${projectId}`, { headers })
      );
      console.log(`SQLiteStorageService: Project ${projectId} deleted`);
      
      // Clear related cache
      this.clearCachePattern('projects:');
      this.clearCachePattern(`project:${projectId}`);
      this.clearCachePattern(`agents:${projectId}`);
      this.clearCachePattern(`vectorstores:${projectId}`);
      
      return true;
    } catch (error) {
      console.error(`SQLiteStorageService: Error deleting project ${projectId}`, error);
      return false;
    }
  }

  // Vector store operations
  async getVectorStores(projectId) {
    const cacheKey = `vectorstores:${projectId}`;
    
    // Check cache first
    const cached = this.getFromCache(cacheKey);
    if (cached !== null) {
      console.log(`SQLiteStorageService: Returning cached vector stores for project ${projectId}:`, cached);
      return cached;
    }
    
    console.log(`SQLiteStorageService: Getting vector stores for project ${projectId}`);
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.get(`/projects/${projectId}/vector-stores`, { headers })
      );
      const vectorStores = response.data || [];
      
      console.log(`SQLiteStorageService: API response for vector stores:`, {
        status: response.status,
        dataType: typeof response.data,
        dataLength: Array.isArray(response.data) ? response.data.length : 'not array',
        data: response.data
      });
      
      // Cache the result
      this.setCache(cacheKey, vectorStores);
      
      return vectorStores;
    } catch (error) {
      console.error(`SQLiteStorageService: Error getting vector stores for project ${projectId}`, error);
      // If we get a 403, clear cache as user permissions might have changed
      if (error.response && error.response.status === 403) {
        console.log('SQLiteStorageService: Got 403 on vector stores, clearing all cache');
        this.clearCache();
      }
      return [];
    }
  }

  async getVectorStore(id) {
    console.log(`SQLiteStorageService: Getting vector store with ID ${id}`);
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.get(`/vector-stores/${id}`, { headers })
      );
      return response.data;
    } catch (error) {
      console.error(`SQLiteStorageService: Error getting vector store ${id}`, error);
      return null;
    }
  }

  async saveVectorStore(vectorStore) {
    console.log(`SQLiteStorageService: Saving vector store ${vectorStore.id || '(new)'} for project ${vectorStore.projectId}`);
    try {
      if (vectorStore.id) {
        // Try update existing vector store - ensure we include projectId in the payload
        const updatePayload = {
          ...vectorStore,
          projectId: vectorStore.projectId // Ensure projectId is always included
        };
        const response = await this.withAuthRequest((headers) =>
          this.api.put(`/vector-stores/${vectorStore.id}`, updatePayload, { headers })
        );
        console.log(`SQLiteStorageService: Vector store ${vectorStore.id} updated successfully`);
        
        // Clear cache for vector stores
        this.clearCachePattern(`vectorstores:${vectorStore.projectId}`);
        
        return response.data;
      } else {
        // Create new vector store
        const response = await this.withAuthRequest((headers) =>
          this.api.post(`/projects/${vectorStore.projectId}/vector-stores`, vectorStore, { headers })
        );
        console.log(`SQLiteStorageService: Vector store created with ID ${response.data.id}`);
        
        // Clear cache for vector stores
        this.clearCachePattern(`vectorstores:${vectorStore.projectId}`);
        
        return response.data;
      }
    } catch (error) {
      console.error(`SQLiteStorageService: Error in saveVectorStore:`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        vectorStoreId: vectorStore.id,
        projectId: vectorStore.projectId,
        errorMessage: error.response?.data?.detail || error.response?.data?.message || error.message,
        endpoint: vectorStore.id ? `PUT /api/vector-stores/${vectorStore.id}` : `POST /api/projects/${vectorStore.projectId}/vector-stores`
      });
      
      // Fallback: if not found on update, attempt create with provided ID
      if (vectorStore.id && error.response && error.response.status === 404) {
        console.warn(`SQLiteStorageService: Update 404 for vector store ${vectorStore.id}, falling back to create`);
        const payload = { ...vectorStore };
        try {
          const response = await this.withAuthRequest((headers) =>
            this.api.post(`/projects/${vectorStore.projectId}/vector-stores`, payload, { headers })
          );
          console.log(`SQLiteStorageService: Vector store created with ID ${response.data.id}`);
          
          // Clear cache for vector stores
          this.clearCachePattern(`vectorstores:${vectorStore.projectId}`);
          
          return response.data;
        } catch (createError) {
          console.error(`SQLiteStorageService: Fallback create failed for vector store ${vectorStore.id}`, {
            status: createError.response?.status,
            data: createError.response?.data,
            message: createError.response?.data?.detail || createError.message
          });
          
          // If it's a 500 error, it might be because the vector store ID already exists
          // in a different project or there's a conflict
          if (createError.response?.status === 500) {
            throw new Error('Unable to save vector store. It may already exist in another project or there is a server conflict.');
          }
          
          throw createError;
        }
      }
      console.error(`SQLiteStorageService: Error saving vector store ${vectorStore.id}`, error);
      throw error;
    }
  }

  async deleteVectorStore(vectorStoreId) {
    console.log(`SQLiteStorageService: Deleting vector store ${vectorStoreId}`);
    try {
      // Get vector store to find project ID for cache clearing
      const vectorStore = await this.getVectorStore(vectorStoreId);
      
      await this.withAuthRequest((headers) =>
        this.api.delete(`/vector-stores/${vectorStoreId}`, { headers })
      );
      console.log(`SQLiteStorageService: Vector store ${vectorStoreId} deleted`);
      
      // Clear cache if we found the project ID
      if (vectorStore && vectorStore.projectId) {
        this.clearCachePattern(`vectorstores:${vectorStore.projectId}`);
      }
      
      return true;
    } catch (error) {
      console.error(`SQLiteStorageService: Error deleting vector store ${vectorStoreId}`, error);
      return false;
    }
  }

  // Agent operations
  async getAgents(projectId) {
    const cacheKey = `agents:${projectId}`;
    
    // Check cache first
    const cached = this.getFromCache(cacheKey);
    if (cached !== null) {
      console.log(`SQLiteStorageService: Returning cached agents for project ${projectId}:`, cached);
      return cached;
    }
    
    console.log(`SQLiteStorageService: Getting agents for project ${projectId}`);
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.get(`/projects/${projectId}/agents`, { headers })
      );
      const agents = response.data || [];
      
      console.log(`SQLiteStorageService: API response for agents:`, {
        status: response.status,
        dataType: typeof response.data,
        dataLength: Array.isArray(response.data) ? response.data.length : 'not array',
        data: response.data
      });
      
      // Cache the result
      this.setCache(cacheKey, agents);
      
      return agents;
    } catch (error) {
      console.error(`SQLiteStorageService: Error getting agents for project ${projectId}`, error);
      // If we get a 403, clear cache as user permissions might have changed
      if (error.response && error.response.status === 403) {
        console.log('SQLiteStorageService: Got 403 on agents, clearing all cache');
        this.clearCache();
      }
      return [];
    }
  }

  async getAgent(agentId) {
    console.log(`SQLiteStorageService: Getting agent ${agentId}`);
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.get(`/agents/${agentId}`, { headers })
      );
      return response.data;
    } catch (error) {
      console.error(`SQLiteStorageService: Error getting agent ${agentId}`, error);
      return null;
    }
  }

  async saveAgent(agent) {
    console.log(`SQLiteStorageService: Saving agent ${agent.id} for project ${agent.projectId}`);
    try {
      let response;
      const requestFn = (headers) => {
        if (agent.id) {
          return this.api.put(`/agents/${agent.id}`, agent, { headers });
        }
        return this.api.post(`/projects/${agent.projectId}/agents`, agent, { headers });
      };
      response = await this.withAuthRequest(requestFn);
      console.log(`SQLiteStorageService: Agent ${agent.id} saved successfully`);
      
      // Clear cache for agents and specific agent
      this.clearCachePattern(`agents:${agent.projectId}`);
      this.clearCachePattern(`agent:${agent.id}`);
      
      // Update the cache with the new agent data to prevent stale reads
      const cacheKey = `agents:${agent.projectId}`;
      const cached = this.getFromCache(cacheKey);
      if (cached !== null) {
        // Update the cached agents array with the new/updated agent
        const updatedAgents = cached.map(a => a.id === response.data.id ? response.data : a);
        if (!cached.find(a => a.id === response.data.id)) {
          updatedAgents.push(response.data);
        }
        this.setCache(cacheKey, updatedAgents);
      }
      
      return response.data;
    } catch (error) {
      console.error(`SQLiteStorageService: Error saving agent ${agent.id}`, error);
      throw error;
    }
  }

  async deleteAgent(agentId) {
    console.log(`SQLiteStorageService: Deleting agent ${agentId}`);
    try {
      // Get agent to find project ID for cache clearing
      const agent = await this.getAgent(agentId);
      
      await this.withAuthRequest((headers) =>
        this.api.delete(`/agents/${agentId}`, { headers })
      );
      console.log(`SQLiteStorageService: Agent ${agentId} deleted`);
      
      // Clear cache if we found the project ID
      if (agent && agent.projectId) {
        this.clearCachePattern(`agents:${agent.projectId}`);
      }
      return true;
    } catch (error) {
      console.error(`SQLiteStorageService: Error deleting agent ${agentId}`, error);
      return false;
    }
  }

  // Layout operations for the agent map
  async getLayout(projectId) {
    console.log(`SQLiteStorageService: Getting agent map layout for project ${projectId}`);
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.get(`/projects/${projectId}/layout`, { headers })
      );
      return response.data;
    } catch (error) {
      console.error(`SQLiteStorageService: Error getting layout for project ${projectId}`, error);
      return { projectId, agents: {}, edges: [] };
    }
  }

  async saveLayout(layout) {
    console.log(`SQLiteStorageService: Saving agent map layout for project ${layout.projectId}`);
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.put(`/projects/${layout.projectId}/layout`, layout, { headers })
      );
      console.log(`SQLiteStorageService: Layout for project ${layout.projectId} saved successfully`);
      
      // Clear layout cache to prevent stale data
      this.clearCachePattern(`layout:${layout.projectId}`);
      
      return response.data;
    } catch (error) {
      console.error(`SQLiteStorageService: Error saving layout for project ${layout.projectId}`, error);
      throw error;
    }
  }

  // MCP Server operations
  async getMCPServers() {
    console.log('SQLiteStorageService: Getting all MCP servers');
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.get('/mcp/servers', { headers })
      );
      return response.data.servers || [];
    } catch (error) {
      console.error('SQLiteStorageService: Error getting MCP servers', error);
      return [];
    }
  }

  async getMCPServer(serverId) {
    console.log(`SQLiteStorageService: Getting MCP server ${serverId}`);
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.get(`/mcp/servers/${serverId}`, { headers })
      );
      return response.data;
    } catch (error) {
      console.error(`SQLiteStorageService: Error getting MCP server ${serverId}`, error);
      return null;
    }
  }

  async saveMCPServer(server) {
    console.log(`SQLiteStorageService: Saving MCP server ${server.id}`);
    try {
      let response;
      const requestFn = (headers) => {
        if (server.id) {
          return this.api.put(`/mcp/servers/${server.id}`, server, { headers });
        }
        return this.api.post('/mcp/servers', server, { headers });
      };
      response = await this.withAuthRequest(requestFn);
      console.log(`SQLiteStorageService: MCP server ${server.id} saved successfully`);
      return response.data;
    } catch (error) {
      console.error(`SQLiteStorageService: Error saving MCP server ${server.id}`, error);
      throw error;
    }
  }

  async deleteMCPServer(serverId) {
    console.log(`SQLiteStorageService: Deleting MCP server ${serverId}`);
    try {
      await this.withAuthRequest((headers) =>
        this.api.delete(`/mcp/servers/${serverId}`, { headers })
      );
      console.log(`SQLiteStorageService: MCP server ${serverId} deleted`);
      return true;
    } catch (error) {
      console.error(`SQLiteStorageService: Error deleting MCP server ${serverId}`, error);
      return false;
    }
  }

  async getProjectMCPServers(projectId) {
    console.log(`SQLiteStorageService: Getting MCP servers for project ${projectId}`);
    try {
      const response = await this.withAuthRequest((headers) =>
        this.api.get(`/mcp/projects/${projectId}/servers`, { headers })
      );
      return response.data.servers || [];
    } catch (error) {
      console.error(`SQLiteStorageService: Error getting MCP servers for project ${projectId}`, error);
      return [];
    }
  }

  async linkMCPServerToProject(serverId, projectId) {
    console.log(`SQLiteStorageService: Linking MCP server ${serverId} to project ${projectId}`);
    try {
      await this.withAuthRequest((headers) =>
        this.api.put(`/mcp/projects/${projectId}/servers/${serverId}`, {}, { headers })
      );
      return true;
    } catch (error) {
      console.error(`SQLiteStorageService: Error linking MCP server ${serverId} to project ${projectId}`, error);
      return false;
    }
  }

  async unlinkMCPServerFromProject(serverId, projectId) {
    console.log(`SQLiteStorageService: Unlinking MCP server ${serverId} from project ${projectId}`);
    try {
      await this.withAuthRequest((headers) =>
        this.api.delete(`/mcp/projects/${projectId}/servers/${serverId}`, { headers })
      );
      return true;
    } catch (error) {
      console.error(`SQLiteStorageService: Error unlinking MCP server ${serverId} from project ${projectId}`, error);
      return false;
    }
  }
}

export default SQLiteStorageService;
